import { AppNS } from 'app';
import { apiGetCompanyById, apiGetCompanyDisciplineById, getAgencies } from 'app/api_v2/companies';
import { apiGetInspectorDisciplineById, apiGetInspectorDisciplines } from 'app/api_v2/inspectors/disciplines';
import { apiGetProfiles } from 'app/api_v2/inspectors/profile';
import { apiGetAdmins } from 'app/api_v2/tickets';
import { AgencyCompany } from 'app/types';
import { FileInfo } from 'app/ui/components/multi-file-uploader2/MultiFileUploader2';

export const TICKET_TYPES: { id: AppNS.TicketType; name: string; isAutomatic: boolean }[] = [
  // AUTOMATIC
  { id: 'agency_profile_verification', name: 'Agency Profile Verification', isAutomatic: true },
  { id: 'inspector_on_time_document_update', name: 'Document Update Verification', isAutomatic: true },
  { id: 'new_inspector_discipline', name: 'Inspector New Discipline', isAutomatic: true },
  { id: 'new_boiler_welder_certificate', name: 'New Boiler Inspector/Welder certificate', isAutomatic: true },
  { id: 'compliance_agency_document_expiration', name: 'Expired Document - Agency', isAutomatic: true },
  { id: 'compliance_inspector_document_expiration', name: 'Expired Document - Inspector', isAutomatic: true },
  { id: 'compliance_agency_acknowledgement_expiration', name: 'Expired Acknowledgement - Agency', isAutomatic: true },
  {
    id: 'compliance_inspector_acknowledgement_expiration',
    name: 'Expired Acknowledgement - Inspector',
    isAutomatic: true,
  },
  { id: 'dispute_missing_geolocation', name: 'Missing Geolocation Data', isAutomatic: true },

  // MANUAL
  { id: 'compliance_manual_audit', name: 'Compliance Manual Audit', isAutomatic: false },
  { id: 'compliance_performance', name: 'Compliance Performance', isAutomatic: false },

  // OTHER
  { id: 'new_agency_id_subticket', name: 'New Agency ID Subticket', isAutomatic: true },
];

export const SUBJECT_TYPES: { id: AppNS.SubjectType; name: string }[] = [
  { id: 'agency', name: 'Agency' },
  { id: 'inspector', name: 'Inspector' },
];

export const DEACTIVATION_SCOPE: { id: AppNS.DeactivationScope; name: string }[] = [
  { id: 'profile', name: 'Profile' },
  { id: 'discipline', name: 'Discipline' },
];

export const TICKET_STATES: { id: AppNS.TicketState; name: string }[] = [
  { id: 'dob_action_required', name: 'DOB Action Required' },
  { id: 'subject_action_required', name: 'Subject Action Required' },
  { id: 'approved', name: 'Approved' },
  { id: 'denied', name: 'Denied' },
  { id: 'cancelled', name: 'Cancelled' },
  { id: 'pending', name: 'Pending' },
];

export const parseFileData = (data: FileInfo[]): FileInfo[] => {
  return data.filter((item) => item.url);
};

export const getSearchValues = (
  search: string,
  type: AppNS.SubjectType | '',
  signal?: AbortSignal
): Promise<{ id: string; name: string }[]> => {
  if (!type) {
    return Promise.resolve([]);
  }

  const apiCall =
    type === 'agency'
      ? getAgencies({ query_search: search, limit: 1000 }, signal)
      : apiGetProfiles({ query_search: search }, signal);

  return apiCall.then((data: AgencyCompany[] | AppNS.InspectorProfile[]) => {
    if (type === 'agency') {
      return data.map((item: AppNS.Params) => ({ id: item.id, name: item?.name || item?.fullName }));
    }

    return data.map((item: AppNS.Params) => ({ id: item.id, name: item?.user.fullName, userId: item.user.id }));
  });
};

export const getDobAdmins = (): Promise<{ id: string; name: string }[]> => {
  return apiGetAdmins({}).then((data) => {
    return data.map((item: AppNS.Params) => ({ id: item.id, name: `${item.firstName} ${item.lastName}` }));
  });
};

export const getDisciplinesValues = (
  id: string,
  type: AppNS.SubjectType | '',
  signal?: AbortSignal
): Promise<{ id: string; name: string }[]> => {
  if (!type) {
    return Promise.resolve([]);
  }

  const apiCall =
    type === 'agency' ? apiGetCompanyById(id, signal) : apiGetInspectorDisciplines({ user_id: id }, signal);

  return apiCall.then((data: AppNS.Company | AppNS.InspectorDiscipline[]) => {
    console.log('getDisciplinesValues', data);
    if (type === 'agency') {
      return (data as AppNS.Company).disciplines.map((item: AppNS.CompanyDiscipline) => ({
        id: item.id,
        name: item.name,
      }));
    }

    return (data as AppNS.InspectorDiscipline[]).map((item: AppNS.InspectorDiscipline) => ({
      id: item.id,
      name: item.name,
    }));
  });
};

export const getDisciplineValue = (
  id: string,
  companyId: string,
  signal?: AbortSignal
): Promise<{ id: string; name: string }> => {
  const apiCall = companyId
    ? apiGetCompanyDisciplineById(id, companyId, signal)
    : apiGetInspectorDisciplineById(id, signal);

  return apiCall.then((data: AppNS.CompanyDiscipline | AppNS.InspectorDiscipline) => {
    console.log('getDisciplineValue', data);
    return {
      id: data.id,
      name: data.name,
    };
  });
};

export const getTypeNameById = (id: string): string => {
  return TICKET_TYPES.find((item) => item.id === id)?.name || '-';
};

export const getStateNameById = (id: string): string => {
  return TICKET_STATES.find((item) => item.id === id)?.name || '-';
};
export const getDeactivationScopeById = (id: string): string => {
  return DEACTIVATION_SCOPE.find((item) => item.id === id)?.name || '-';
};

export const getTicketLink = (id: string, type?: 'bo' | 'agency'): string => {
  if (type === 'agency') {
    return `/agency/tickets/${id}`;
  }
  // Default to backoffice
  return `/backoffice/tickets/${id}`;
};
